import { assertNever, Hex, raise } from '@sparkdotfi/common-universal'
import { useQueryClient } from '@tanstack/react-query'
import { Log, WalletCallReceipt } from 'viem'

import { Action, ActionContext, BatchActionHandler, BatchActionHandlerState } from './types'
import { actionToConfig } from './useContractAction'
import { parseWriteErrorMessage } from './utils'
import { runBatchPreWriteLifecycle } from '@/features/actions/logic/preWriteLifecycle'
import type { Eip5792WriteStatus } from '@/features/actions/logic/transactions/useEip5792Write'
import { useEip5792Write } from '@/features/actions/logic/transactions/useEip5792Write'

export interface UseBatchActionHandlerParams {
  actions: Action[]
  enabled: boolean
  onFinish?: () => void
  context: ActionContext
}

export function useBatchActionHandler({
  actions,
  enabled,
  onFinish,
  context,
}: UseBatchActionHandlerParams): BatchActionHandler {
  const queryClient = useQueryClient()

  const batchConfigs = actions.map((action) => actionToConfig(action, context))

  const contracts = batchConfigs.map((config) => config.getWriteConfig())
  function invalidateQueries(): void {
    for (const config of batchConfigs) {
      const queryKeys = config.invalidates()
      for (const queryKey of queryKeys) {
        void queryClient.invalidateQueries({ queryKey })
      }
    }
  }
  function onTransactionSettled(txReceipts: WalletCallReceipt<bigint, 'success' | 'reverted'>[]): void {
    // @note Assuming there is only one receipt for the whole batch for now
    const txReceipt = txReceipts[0] ?? raise('No transaction receipt')

    invalidateQueries()
    if (txReceipt.status === 'success') {
      for (const config of batchConfigs) {
        config.onSuccessfulAction?.({
          ...txReceipt,
          logs: mapToFullLogs(txReceipt.logs),
        })
      }
    }
    onFinish?.()
  }

  const { write, status, reset } = useEip5792Write({
    enabled,
    contracts,
    callbacks: { onTransactionSettled: { mounted: onTransactionSettled, unmounted: invalidateQueries } },
    onBeforeWrite: () =>
      runBatchPreWriteLifecycle({
        actions: batchConfigs.map((config, index) => ({
          contract: contracts[index] ?? raise('Missing write config for batch action'),
          beforeWriteCheck: config.beforeWriteCheck,
        })),
        chainId: context.chainId,
      }),
  })

  return {
    actions,
    onAction: write,
    state: mapBatchWriteStatusToBatchActionState(status),
    reset,
  }
}

function mapBatchWriteStatusToBatchActionState(status: Eip5792WriteStatus): BatchActionHandlerState {
  switch (status.kind) {
    case 'disabled':
      return { status: 'disabled' }

    case 'ready':
      return { status: 'ready' }

    case 'tx-sending':
      return { status: 'loading' }

    case 'tx-confirming':
      return { status: 'loading' }

    case 'success':
      return { status: 'success' }

    case 'error': {
      const errorKind = (() => {
        if (status.errorKind === 'submission') {
          return 'batch-submission' as const
        }
        if (status.errorKind === 'confirmation') {
          return 'batch-confirmation' as const
        }
        if (status.errorKind === 'tx-reverted') {
          return 'batch-item-tx-reverted' as const
        }
        if (status.errorKind === 'before-write') {
          return 'before-write' as const
        }
        return 'wallet-upgrade-rejection' as const
      })()
      return {
        status: 'error',
        errorKind,
        message: parseWriteErrorMessage(status.error, { isBatchWrite: true }),
      }
    }

    default:
      assertNever(status)
  }
}

function mapToFullLogs(logs: WalletCallReceipt<bigint, 'success' | 'reverted'>['logs']): Log[] {
  return logs.map((log) => ({
    address: log.address,
    topics: log.topics as [Hex, ...Hex[]],
    data: log.data,
    blockHash: null,
    blockNumber: null,
    logIndex: null,
    transactionHash: null,
    transactionIndex: null,
    removed: false,
  }))
}
